'use client';

import { PortableText } from 'next-sanity';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { richTextComponent } from '@/sanity/sanityPortableText';
import {
  PageHomeSchema,
  PageHomeVideoItem,
} from '@/sanity/pages/PageHomeTemplate';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';
import { useCarouselApi } from '@/hooks/useCarouselApi';
import dotsGradientBg from '@/public/global/dots-gradient-bg.png';
import Image from 'next/image';
import { getYouTubeEmbedUrl } from '@/lib/youtube';

const ease = [0.25, 0.1, 0.25, 1] as const;

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 12.75 10.625"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.4375 0L6.67781 0.740031L10.7047 4.78125H0V5.84375H10.7047L6.67781 9.86691L7.4375 10.625L12.75 5.3125L7.4375 0Z"
        fill="currentColor"
      />
    </svg>
  );
}

function VideoSlide({ item }: { item: PageHomeVideoItem }) {
  const embedUrl = item.video?.embedUrl;
  if (!embedUrl) return null;

  return (
    <div className="flex flex-col gap-[1.75rem]">
      <div className="relative aspect-video w-full overflow-hidden rounded-[0.5rem] bg-black">
        <iframe
          src={getYouTubeEmbedUrl(embedUrl)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 h-full w-full rounded-[0.5rem]"
        />
      </div>
      {item.title && (
        <p className="mb-0 font-geist text-[2.25rem] font-[500] leading-[1.4] tracking-[-0.07rem] text-black">
          {item.title}
        </p>
      )}
    </div>
  );
}

export default function ContentVideosSection({
  data,
}: {
  data: PageHomeSchema;
}) {
  const videos = data.contentVideos ?? [];
  const { setApi, handlePrev, handleNext, current, count, isFirst, isLast } =
    useCarouselApi();

  if (!data.contentVideosChip && !data.contentVideosTitle && !videos.length)
    return null;

  const progress = count > 1 ? (current - 1) / (count - 1) : 1;

  return (
    <section className="w-full bg-white relative py-[10rem] lg:py-[20rem] flex flex-col items-center gap-[6rem] lg:gap-[8rem]">
      <div className="absolute w-[calc(100%+50rem)] lg:w-full h-[48rem] top-0 left-1/2 -translate-x-1/2 mix-blend-exclusion pointer-events-none select-none">
        <Image fill alt="Background pattern" src={dotsGradientBg} />
      </div>
      <div className="mx-auto flex w-[min(120rem,calc(100%-4rem))] flex-col items-center gap-[2rem] text-center">
        {data.contentVideosChip && (
          <p className="mb-0 font-blacker text-[2rem] italic leading-[1.5] text-black">
            {data.contentVideosChip}
          </p>
        )}
        {data.contentVideosTitle && (
          <div className="richtext flex flex-col gap-[1rem] prose-em:font-blacker prose-em:italic prose-em:text-[#003828]">
            <PortableText
              value={data.contentVideosTitle}
              components={richTextComponent}
            />
          </div>
        )}
        {data.contentVideosSubtitle && (
          <p className="mb-0 font-geist text-[2rem] leading-[1.5] tracking-[-0.04rem] text-black/60">
            {data.contentVideosSubtitle}
          </p>
        )}
      </div>

      {videos.length > 0 && (
        <div className="flex flex-col gap-[4rem] w-[min(170rem,calc(100%-4rem))]">
          <Carousel
            setApi={setApi}
            opts={{ align: 'start', loop: false }}
            className="w-full overflow-hidden px-[2rem] lg:px-[4rem]"
          >
            <CarouselContent className="gap-[2rem]">
              {videos.map((item) => (
                <CarouselItem
                  key={item._key}
                  className="pl-0 basis-full lg:basis-1/3"
                >
                  <VideoSlide item={item} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {count > 1 && (
            <div className="mx-auto flex w-[min(52rem,calc(100%-4rem))] items-center gap-[2rem]">
              <button
                type="button"
                onClick={handlePrev}
                disabled={isFirst}
                aria-label="Previous"
                className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full bg-black/10 transition-opacity disabled:opacity-30 hover:opacity-70"
              >
                <ArrowIcon className="h-[2.125rem] w-[2.125rem] rotate-180 text-black" />
              </button>

              <div className="h-[0.5rem] flex-1 overflow-hidden rounded-[3rem] bg-black/10">
                <motion.div
                  className="h-full rounded-full bg-black"
                  animate={{ width: `${progress * 100}%` }}
                  transition={{ duration: 0.3, ease }}
                />
              </div>

              <button
                type="button"
                onClick={handleNext}
                disabled={isLast}
                aria-label="Next"
                className="flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full bg-black transition-opacity disabled:opacity-30 hover:opacity-80"
              >
                <ArrowIcon className="h-[2.125rem] w-[2.125rem] text-white" />
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
